import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronRight,
  Cloud,
  File,
  FileText,
  Folder,
  FolderInput,
  HardDrive,
  Home,
  Info,
  Loader2,
  RefreshCw,
  Search,
  ToggleLeft,
  ToggleRight,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { INDEXABLE_EXTENSIONS } from "@/constants/files";
import { sharepointService } from "@/lib/services";
import type { SharePointDrive, SharePointItem, SharePointSite } from "@/types";
import { SourcePicker } from "./SourcePicker";

// ---------------------------------------------------------------------------
// Constants — folder sync limits
// ---------------------------------------------------------------------------

/** Show a warning when the folder has more files than this. */
const FOLDER_WARN_THRESHOLD = 50;
/** Block selection when the folder has more files than this. */
const FOLDER_HARD_LIMIT = 500;

/** File extensions that the backend can index (derived from ``@/constants/files``). */
const SUPPORTED_EXTENSIONS = INDEXABLE_EXTENSIONS;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SharePointFolderSelection {
  folder: SharePointItem;
  recursive: boolean;
  fileCount: number;
  /** Path segments leading to the selected folder. */
  folderPath: string;
}

export interface SharePointSelection {
  site: SharePointSite;
  drive: SharePointDrive;
  items: SharePointItem[];
  /** Set when the user selected a folder instead of individual files. */
  folderSelection?: SharePointFolderSelection;
}

interface BreadcrumbEntry {
  label: string;
  folderId?: string; // undefined = drive root
}

interface SharePointBrowserProps {
  /** Called when the user confirms their selection. */
  onSelect: (selection: SharePointSelection) => void;
  /** Called when the user cancels / closes. */
  onCancel: () => void;
  /** Allow selecting multiple files (default true). */
  multiSelect?: boolean;
  /** Only show files (hide folders from selection). */
  filesOnly?: boolean;
  /** Allow selecting entire folders (default false). */
  allowFolderSelect?: boolean;
  /**
   * When true, show an initial step letting the user choose between browsing
   * their personal OneDrive or SharePoint sites. Default false (SharePoint only).
   */
  allowOneDrive?: boolean;
  /** Optional title override for the header. */
  title?: string;
  /**
   * When true, hides the internal Cancel/Select footer and instead fires
   * `onSelect` automatically whenever the selection state changes.
   * Fires `onClear` when the selection is removed.
   * Useful when the browser is embedded in a larger form that has its own
   * submit button.
   */
  embedded?: boolean;
  /** Called when the selection is cleared (embedded mode only). */
  onClear?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFileSize(bytes?: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function getFileIcon(item: SharePointItem) {
  if (item.is_folder) return Folder;
  const ext = item.name.split(".").pop()?.toLowerCase();
  if (["pdf", "doc", "docx", "txt", "md", "rtf"].includes(ext || "")) return FileText;
  return File;
}

/** Return true if the file has a supported (indexable) extension. */
function isSupportedFile(item: SharePointItem): boolean {
  if (item.is_folder) return true; // folders are always navigable
  const dot = item.name.lastIndexOf(".");
  if (dot === -1) return false;
  return SUPPORTED_EXTENSIONS.has(item.name.slice(dot).toLowerCase());
}

/** Small pill describing a site's type (Team / Communication / OneDrive). */
function siteTypeBadge(siteType?: SharePointSite["site_type"]) {
  const config: Record<string, { label: string; className: string }> = {
    team: { label: "Team site", className: "bg-purple-500/10 text-purple-300" },
    communication: { label: "Communication site", className: "bg-blue-500/10 text-blue-300" },
    personal: { label: "OneDrive", className: "bg-emerald-500/10 text-emerald-300" },
  };
  const entry = siteType ? config[siteType] : undefined;
  if (!entry) return null;
  return (
    <span className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${entry.className}`}>
      {entry.label}
    </span>
  );
}

/** Human-readable loading/subtitle text for each navigation step. */
function stepLabel(step: "source" | "sites" | "drives" | "items", variant: "loading" | "search" | "subtitle"): string {
  const labels: Record<string, Record<string, string>> = {
    source: { loading: "", search: "", subtitle: "Choose a source" },
    sites: { loading: "Loading sites...", search: "Search sites...", subtitle: "Select a SharePoint site" },
    drives: { loading: "Loading libraries...", search: "Filter libraries...", subtitle: "" },
    items: { loading: "Loading files...", search: "Search files...", subtitle: "" },
  };
  return labels[step]?.[variant] ?? "";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SharePointBrowser({
  // NOSONAR -- multi-step navigation browser with site/drive/item selection
  onSelect,
  onCancel,
  multiSelect = true,
  filesOnly = false,
  allowFolderSelect = false,
  allowOneDrive = false,
  title,
  embedded = false,
  onClear,
}: Readonly<SharePointBrowserProps>) {
  // Navigation state
  type Step = "source" | "sites" | "drives" | "items";
  const [step, setStep] = useState<Step>(allowOneDrive ? "source" : "sites");

  // Data
  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [drives, setDrives] = useState<SharePointDrive[]>([]);
  const [items, setItems] = useState<SharePointItem[]>([]);

  // Selected context
  const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null);
  const [selectedDrive, setSelectedDrive] = useState<SharePointDrive | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbEntry[]>([]);

  // Folder selection state
  const [selectedFolder, setSelectedFolder] = useState<SharePointItem | null>(null);
  const [folderRecursive, setFolderRecursive] = useState(false);
  const [folderFileCount, setFolderFileCount] = useState<number | null>(null);
  const [folderTotalCount, setFolderTotalCount] = useState<number | null>(null);
  const [folderCountTruncated, setFolderCountTruncated] = useState(false);
  const [folderCountLoading, setFolderCountLoading] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SharePointItem[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  // True when a OneDrive load failed on the source step, so Retry knows to re-attempt it.
  const [pendingOneDriveRetry, setPendingOneDriveRetry] = useState(false);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const loadSites = useCallback(async (query?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await sharepointService.searchSites(query || undefined);
      setSites(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load SharePoint sites";
      if (msg.includes("403") || msg.includes("consent")) {
        setError(
          "SharePoint access requires admin consent. Please contact your administrator to grant the required permissions.",
        );
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDrives = useCallback(async (siteId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await sharepointService.listDrives(siteId);
      setDrives(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load document libraries");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadItems = useCallback(async (driveId: string, folderId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await sharepointService.listItems(driveId, {
        folderId: folderId || undefined,
      });
      setItems(data.items);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load files");
    } finally {
      setLoading(false);
    }
  }, []);

  // Synthetic "site" representing the user's OneDrive, so downstream consumers
  // (which expect selection.site) keep working without a real SharePoint site.
  const makeOneDriveSite = useCallback(
    (drive: SharePointDrive): SharePointSite => ({
      id: `onedrive:${drive.id}`,
      display_name: "My OneDrive",
      web_url: drive.web_url,
      is_personal_site: true,
      site_type: "personal",
    }),
    [],
  );

  const loadOneDrive = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPendingOneDriveRetry(false);
    try {
      const drive = await sharepointService.getMyOneDrive();
      const odSite = makeOneDriveSite(drive);
      setSelectedSite(odSite);
      setSelectedDrive(drive);
      setBreadcrumbs([{ label: drive.name || "OneDrive" }]);
      setSelectedItems(new Set());
      setStep("items");
      await loadItems(drive.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load OneDrive";
      if (msg.includes("403") || msg.includes("consent")) {
        setError(
          "OneDrive access requires admin consent. Please contact your administrator to grant the required permissions.",
        );
      } else {
        setError(msg);
      }
      setPendingOneDriveRetry(true);
    } finally {
      setLoading(false);
    }
  }, [makeOneDriveSite, loadItems]);

  // Load sites on mount only when starting directly on the sites step
  // (i.e. OneDrive picker disabled). Otherwise wait for the user's choice.
  useEffect(() => {
    if (!allowOneDrive) loadSites();
  }, [loadSites, allowOneDrive]);

  // -------------------------------------------------------------------------
  // Navigation handlers
  // -------------------------------------------------------------------------

  const handleSelectSite = (site: SharePointSite) => {
    setSelectedSite(site);
    setStep("drives");
    setSearchQuery("");
    setSearchResults(null);
    loadDrives(site.id);
  };

  const handleSelectDrive = (drive: SharePointDrive) => {
    setSelectedDrive(drive);
    setStep("items");
    setBreadcrumbs([{ label: drive.name }]);
    setSearchQuery("");
    setSearchResults(null);
    setSelectedItems(new Set());
    loadItems(drive.id);
  };

  const handleOpenFolder = (folder: SharePointItem) => {
    if (!selectedDrive) return;
    setBreadcrumbs((prev) => [...prev, { label: folder.name, folderId: folder.id }]);
    setSearchQuery("");
    setSearchResults(null);
    loadItems(selectedDrive.id, folder.id);
  };

  const handleBreadcrumbNav = (index: number) => {
    if (!selectedDrive) return;
    const newCrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newCrumbs);
    setSearchQuery("");
    setSearchResults(null);
    const folderId = newCrumbs[newCrumbs.length - 1]?.folderId;
    loadItems(selectedDrive.id, folderId);
  };

  const handleBack = () => {
    setError(null);
    setSearchQuery("");
    setSearchResults(null);
    if (step === "items" && breadcrumbs.length > 1) {
      // Inside a subfolder — go up one level
      const newCrumbs = breadcrumbs.slice(0, -1);
      setBreadcrumbs(newCrumbs);
      const folderId = newCrumbs[newCrumbs.length - 1]?.folderId;
      loadItems(selectedDrive!.id, folderId);
      return;
    }
    if (step === "items") {
      // OneDrive path has no site/drive steps — go back to the source picker.
      if (selectedSite?.id.startsWith("onedrive:")) {
        setStep("source");
        setSelectedSite(null);
        setSelectedDrive(null);
        setBreadcrumbs([]);
        setItems([]);
        return;
      }
      setStep("drives");
      setSelectedDrive(null);
      setBreadcrumbs([]);
      return;
    }
    if (step === "drives") {
      setStep("sites");
      setSelectedSite(null);
      setDrives([]);
      return;
    }
    if (step === "sites" && allowOneDrive) {
      setStep("source");
      setSites([]);
    }
  };

  const handleChooseOneDrive = () => {
    setError(null);
    setSearchQuery("");
    setSearchResults(null);
    loadOneDrive();
  };

  const handleChooseSharePoint = () => {
    setError(null);
    setSearchQuery("");
    setSearchResults(null);
    setStep("sites");
    if (sites.length === 0) loadSites();
  };

  // -------------------------------------------------------------------------
  // Folder count fetching
  // -------------------------------------------------------------------------

  const loadFolderCount = useCallback(async (driveId: string, folderId: string, recursive: boolean) => {
    setFolderCountLoading(true);
    try {
      const data = await sharepointService.countFolderItems(driveId, folderId, recursive);
      setFolderFileCount(data.file_count);
      setFolderTotalCount(data.total_count ?? data.file_count);
      setFolderCountTruncated(data.truncated);
    } catch {
      setFolderFileCount(null);
      setFolderTotalCount(null);
      setFolderCountTruncated(false);
    } finally {
      setFolderCountLoading(false);
    }
  }, []);

  // Reload count when recursive toggle changes
  useEffect(() => {
    if (selectedFolder && selectedDrive) {
      loadFolderCount(selectedDrive.id, selectedFolder.id, folderRecursive);
    }
  }, [selectedFolder, selectedDrive, folderRecursive, loadFolderCount]);

  // -------------------------------------------------------------------------
  // Selection
  // -------------------------------------------------------------------------

  const toggleItemSelection = (item: SharePointItem) => {
    if (filesOnly && item.is_folder) return;
    // Reject unsupported file types
    if (!item.is_folder && !isSupportedFile(item)) return;

    // If selecting a folder in folder-select mode
    if (allowFolderSelect && item.is_folder) {
      if (selectedFolder?.id === item.id) {
        // Deselect folder
        setSelectedFolder(null);
        setFolderFileCount(null);
        setFolderTotalCount(null);
        setFolderCountTruncated(false);
      } else {
        // Select this folder (clears individual file selection)
        setSelectedFolder(item);
        setSelectedItems(new Set());
      }
      return;
    }

    // Individual file selection — clears folder selection
    if (selectedFolder) {
      setSelectedFolder(null);
      setFolderFileCount(null);
      setFolderTotalCount(null);
      setFolderCountTruncated(false);
    }

    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        if (!multiSelect) next.clear();
        next.add(item.id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    if (!selectedSite || !selectedDrive) return;

    // Folder selection mode
    if (selectedFolder && folderFileCount !== null) {
      const folderPath = breadcrumbs
        .slice(1)
        .map((b) => b.label)
        .concat(selectedFolder.name)
        .join("/");

      onSelect({
        site: selectedSite,
        drive: selectedDrive,
        items: [],
        folderSelection: {
          folder: selectedFolder,
          recursive: folderRecursive,
          fileCount: folderFileCount,
          folderPath: folderPath || selectedFolder.name,
        },
      });
      return;
    }

    // Individual file selection mode
    if (selectedItems.size === 0) return;

    const allItems = searchResults || items;
    const selected = allItems.filter((i) => selectedItems.has(i.id));

    onSelect({
      site: selectedSite,
      drive: selectedDrive,
      items: selected,
    });
  };

  // -------------------------------------------------------------------------
  // Embedded mode: auto-fire onSelect whenever selection changes
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!embedded || !selectedSite || !selectedDrive) return;

    // Folder selected — wait for count to finish loading
    if (selectedFolder) {
      if (folderCountLoading || folderFileCount === null) return;
      if (folderFileCount === 0 || folderFileCount > FOLDER_HARD_LIMIT) {
        onClear?.();
        return;
      }

      const folderPath = breadcrumbs
        .slice(1)
        .map((b) => b.label)
        .concat(selectedFolder.name)
        .join("/");

      onSelect({
        site: selectedSite,
        drive: selectedDrive,
        items: [],
        folderSelection: {
          folder: selectedFolder,
          recursive: folderRecursive,
          fileCount: folderFileCount,
          folderPath: folderPath || selectedFolder.name,
        },
      });
      return;
    }

    // Individual file selection
    if (selectedItems.size > 0) {
      const allItems = searchResults || items;
      const selected = allItems.filter((i) => selectedItems.has(i.id));
      onSelect({
        site: selectedSite,
        drive: selectedDrive,
        items: selected,
      });
      return;
    }

    // Nothing selected — clear parent state
    onClear?.();
  }, [
    embedded,
    selectedSite,
    selectedDrive,
    selectedFolder,
    selectedItems,
    folderRecursive,
    folderFileCount,
    folderCountLoading,
  ]);

  // -------------------------------------------------------------------------
  // Retry on error
  // -------------------------------------------------------------------------

  const handleRetry = () => {
    setError(null);
    if (step === "source") {
      // The only loadable action on the source step is a failed OneDrive load.
      if (pendingOneDriveRetry) return loadOneDrive();
      return;
    }
    if (step === "sites") return loadSites();
    if (step === "drives" && selectedSite) return loadDrives(selectedSite.id);
    if (step === "items" && selectedDrive) {
      const folderId = breadcrumbs[breadcrumbs.length - 1]?.folderId;
      return loadItems(selectedDrive.id, folderId);
    }
  };

  // -------------------------------------------------------------------------
  // Search (for items step only)
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (step !== "items" || !selectedDrive || !searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await sharepointService.searchItems(selectedDrive.id, searchQuery.trim());
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, step, selectedDrive]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const displayItems = searchResults ?? items;

  // Sort: folders first, then files alphabetically
  const sortedItems = [...displayItems].sort((a, b) => {
    if (a.is_folder && !b.is_folder) return -1;
    if (!a.is_folder && b.is_folder) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="flex flex-col h-full max-h-[70vh]">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-border-primary">
        {step !== (allowOneDrive ? "source" : "sites") && (
          <button
            onClick={handleBack}
            className="p-1.5 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors"
            title="Go back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <div className="h-8 w-8 rounded-lg bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center shrink-0">
          <Cloud className="h-4 w-4 text-brand-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-content-primary truncate">{title || "Browse SharePoint"}</h3>
          <p className="text-xs text-content-quaternary truncate">
            {step === "source" && "Choose a source"}
            {step === "sites" && stepLabel(step, "subtitle")}
            {step === "drives" && selectedSite?.display_name}
            {step === "items" && selectedDrive?.name}
          </p>
        </div>
        {step === "items" && (selectedItems.size > 0 || selectedFolder) && (
          <span className="text-xs text-brand-primary font-medium px-2 py-1 bg-brand-primary-subtle rounded-full">
            {selectedFolder ? "1 folder" : `${selectedItems.size} selected`}
          </span>
        )}
      </div>

      {/* Breadcrumbs (items step only) */}
      {step === "items" && breadcrumbs.length > 0 && (
        <div className="flex items-center gap-1 px-4 py-2 border-b border-border-primary overflow-x-auto text-xs">
          <button
            onClick={() => handleBreadcrumbNav(0)}
            className="flex items-center gap-1 text-content-tertiary hover:text-content-primary transition-colors shrink-0"
          >
            <Home className="h-3 w-3" />
          </button>
          {breadcrumbs.slice(1).map((crumb, idx) => (
            <span key={crumb.folderId || `crumb-${crumb.label}-${idx}`} className="flex items-center gap-1 shrink-0">
              <ChevronRight className="h-3 w-3 text-content-quaternary" />
              <button
                onClick={() => handleBreadcrumbNav(idx + 1)}
                className={`hover:text-content-primary transition-colors ${
                  idx + 1 === breadcrumbs.length - 1 ? "text-content-primary font-medium" : "text-content-tertiary"
                }`}
              >
                {crumb.label}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search bar (hidden on the source picker) */}
      {step !== "source" && (
        <div className="px-4 py-2 border-b border-border-primary">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-content-quaternary" />
            <input
              type="text"
              placeholder={stepLabel(step, "search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-content-tertiary animate-spin" />
            )}
          </div>
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {(() => {
          if (loading) {
            return (
              <div className="flex flex-col items-center justify-center py-12 text-content-tertiary">
                <Loader2 className="h-6 w-6 animate-spin text-brand-primary mb-3" />
                <p className="text-sm">{stepLabel(step, "loading")}</p>
              </div>
            );
          }
          if (error) {
            return (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <AlertCircle className="h-8 w-8 text-red-400 mb-3" />
                <p className="text-sm text-red-400 mb-4">{error}</p>
                <button
                  onClick={handleRetry}
                  className="flex items-center gap-2 text-sm text-content-secondary hover:text-content-primary transition-colors"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Retry
                </button>
              </div>
            );
          }
          if (step === "source") {
            return <SourcePicker onChooseOneDrive={handleChooseOneDrive} onChooseSharePoint={handleChooseSharePoint} />;
          }
          if (step === "sites") {
            return (
              // ------- Sites list -------
              <div className="divide-y divide-border-primary/50">
                {sites
                  .filter((s) => !searchQuery || s.display_name.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((site) => (
                    <button
                      key={site.id}
                      onClick={() => handleSelectSite(site)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-secondary/50 transition-colors text-left"
                    >
                      <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                        <Cloud className="h-4 w-4 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-content-primary truncate">{site.display_name}</p>
                          {siteTypeBadge(site.site_type)}
                        </div>
                        {site.description && (
                          <p className="text-xs text-content-quaternary truncate">{site.description}</p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-content-quaternary shrink-0" />
                    </button>
                  ))}
                {sites.length === 0 && (
                  <div className="py-12 text-center text-sm text-content-quaternary">No SharePoint sites found</div>
                )}
              </div>
            );
          }
          if (step === "drives") {
            return (
              // ------- Drives list -------
              <div className="divide-y divide-border-primary/50">
                {drives
                  .filter((d) => !searchQuery || d.name.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((drive) => (
                    <button
                      key={drive.id}
                      onClick={() => handleSelectDrive(drive)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-secondary/50 transition-colors text-left"
                    >
                      <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                        <HardDrive className="h-4 w-4 text-purple-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-content-primary truncate">{drive.name}</p>
                        {drive.description && (
                          <p className="text-xs text-content-quaternary truncate">{drive.description}</p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-content-quaternary shrink-0" />
                    </button>
                  ))}
                {drives.length === 0 && (
                  <div className="py-12 text-center text-sm text-content-quaternary">No document libraries found</div>
                )}
              </div>
            );
          }
          // ------- Items list (files & folders) -------
          return (
            <div className="divide-y divide-border-primary/50">
              {/* Folder selection panel */}
              {selectedFolder && (
                <div className="px-4 py-3 bg-brand-primary/5 border-b border-brand-primary/20">
                  <div className="flex items-center gap-2 mb-2">
                    <FolderInput className="h-4 w-4 text-brand-primary" />
                    <span className="text-sm font-medium text-content-primary">Folder: {selectedFolder.name}</span>
                    <button
                      onClick={() => {
                        setSelectedFolder(null);
                        setFolderFileCount(null);
                        setFolderTotalCount(null);
                        setFolderCountTruncated(false);
                      }}
                      className="ml-auto text-xs text-content-tertiary hover:text-content-primary transition-colors"
                    >
                      Clear
                    </button>
                  </div>

                  {/* Recursive toggle */}
                  <button
                    onClick={() => setFolderRecursive((v) => !v)}
                    className="flex items-center gap-2 text-sm text-content-secondary hover:text-content-primary transition-colors mb-2"
                  >
                    {folderRecursive ? (
                      <ToggleRight className="h-5 w-5 text-brand-primary" />
                    ) : (
                      <ToggleLeft className="h-5 w-5 text-content-quaternary" />
                    )}
                    <span>Include sub-folders</span>
                  </button>

                  {/* File count / warnings */}
                  {folderCountLoading && (
                    <div className="flex items-center gap-2 text-xs text-content-tertiary">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Counting files...</span>
                    </div>
                  )}
                  {!folderCountLoading && folderFileCount !== null && (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs">
                        <Info className="h-3 w-3 text-content-quaternary shrink-0" />
                        <span className="text-content-secondary">
                          {folderTotalCount !== null && folderTotalCount > folderFileCount ? (
                            <>
                              {folderCountTruncated ? `${folderFileCount}+` : folderFileCount} of{" "}
                              {folderCountTruncated ? `${folderTotalCount}+` : folderTotalCount} file
                              {folderTotalCount === 1 ? "" : "s"} supported
                              {folderRecursive ? " (including sub-folders)" : ""}
                            </>
                          ) : (
                            <>
                              {folderCountTruncated ? `${folderFileCount}+` : folderFileCount} file
                              {folderFileCount === 1 ? "" : "s"} found
                              {folderRecursive ? " (including sub-folders)" : ""}
                            </>
                          )}
                        </span>
                      </div>
                      {folderFileCount > FOLDER_HARD_LIMIT && (
                        <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 px-2 py-1.5 rounded-sm">
                          <XCircle className="h-3.5 w-3.5 shrink-0" />
                          <span>
                            This folder exceeds the {FOLDER_HARD_LIMIT}-file limit. Please select a smaller folder or
                            pick individual files.
                          </span>
                        </div>
                      )}
                      {folderFileCount > FOLDER_WARN_THRESHOLD && folderFileCount <= FOLDER_HARD_LIMIT && (
                        <div className="flex items-center gap-2 text-xs text-yellow-400 bg-yellow-500/10 px-2 py-1.5 rounded-sm">
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                          <span>Large folder — syncing {folderFileCount} files may take several minutes.</span>
                        </div>
                      )}
                      {folderFileCount === 0 && (
                        <div className="flex items-center gap-2 text-xs text-content-quaternary">
                          <AlertCircle className="h-3 w-3 shrink-0" />
                          <span>This folder contains no files{folderRecursive ? " (including sub-folders)" : ""}.</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {sortedItems.map((item) => {
                const ItemIcon = getFileIcon(item);
                const isFileSelected = selectedItems.has(item.id);
                const isFolderSelected = selectedFolder?.id === item.id;
                const isSelected = isFileSelected || isFolderSelected;
                const supported = isSupportedFile(item);
                const isSelectable =
                  supported && ((allowFolderSelect && item.is_folder) || !filesOnly || !item.is_folder);

                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                      isSelected ? "bg-brand-primary/5" : !supported ? "opacity-50" : "hover:bg-surface-secondary/50"
                    }`}
                    title={!supported ? "This file type cannot be indexed" : undefined}
                  >
                    {/* Selection checkbox (for files, or folders if allowFolderSelect) */}
                    {isSelectable && (
                      <button
                        onClick={() => toggleItemSelection(item)}
                        className={`flex items-center justify-center h-5 w-5 rounded border transition-colors shrink-0 ${
                          isSelected
                            ? "bg-brand-primary border-brand-primary"
                            : "border-border-hover hover:border-content-tertiary"
                        }`}
                      >
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </button>
                    )}
                    {!isSelectable && <div className="w-5 shrink-0" />}

                    {/* Icon + name (clickable for folders to navigate, clickable for supported files to select) */}
                    <button
                      onClick={() => {
                        if (item.is_folder) {
                          handleOpenFolder(item);
                        } else if (supported) {
                          toggleItemSelection(item);
                        }
                      }}
                      className={`flex items-center gap-3 flex-1 min-w-0 text-left ${
                        !supported && !item.is_folder ? "cursor-default" : ""
                      }`}
                      disabled={!supported && !item.is_folder}
                    >
                      <ItemIcon
                        className={`h-4 w-4 shrink-0 ${
                          item.is_folder
                            ? "text-yellow-400"
                            : supported
                              ? "text-content-tertiary"
                              : "text-content-quaternary"
                        }`}
                      />
                      <span
                        className={`text-sm truncate ${supported ? "text-content-primary" : "text-content-quaternary"}`}
                      >
                        {item.name}
                      </span>
                      {!supported && (
                        <span className="text-[10px] text-content-quaternary bg-surface-tertiary px-1.5 py-0.5 rounded-sm shrink-0">
                          Unsupported
                        </span>
                      )}
                    </button>

                    {/* Meta */}
                    <div className="flex items-center gap-3 shrink-0 text-xs text-content-quaternary">
                      {!item.is_folder && item.size != null && <span>{formatFileSize(item.size)}</span>}
                      {item.is_folder && item.child_count != null && <span>{item.child_count} items</span>}
                      {item.is_folder && <ChevronRight className="h-3.5 w-3.5 text-content-quaternary" />}
                    </div>
                  </div>
                );
              })}
              {sortedItems.length === 0 && (
                <div className="py-12 text-center text-sm text-content-quaternary">
                  {searchQuery ? "No files match your search" : "This folder is empty"}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Footer — hidden in embedded mode since the parent form handles submit */}
      {!embedded && (
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-content-secondary hover:text-content-primary transition-colors"
          >
            Cancel
          </button>
          {step === "items" && (
            <button
              onClick={handleConfirm}
              disabled={
                (selectedItems.size === 0 && !selectedFolder) ||
                (selectedFolder !== null &&
                  (folderFileCount === null ||
                    folderFileCount === 0 ||
                    folderFileCount > FOLDER_HARD_LIMIT ||
                    folderCountLoading))
              }
              className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white text-sm rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(() => {
                if (selectedFolder) {
                  return `Select folder (${folderCountLoading ? "..." : (folderFileCount ?? "?")} files)`;
                }
                if (!multiSelect) return "Select";
                const plural = selectedItems.size === 1 ? "" : "s";
                return `Select ${selectedItems.size || ""} file${plural}`;
              })()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
